import PropTypes from "prop-types"
import React, { useState } from "react"

import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
  Spinner,
} from "reactstrap"

//redux
import { useSelector, useDispatch } from "react-redux"
import { createSelector } from "reselect"
import { Link, useNavigate } from "react-router-dom"
import withRouter from "components/Common/withRouter"

// Formik validation
import * as Yup from "yup"
import { useFormik } from "formik"

// actions

// import images
import logo from "assets/images/logo.svg"
import { useTranslation } from "react-i18next"
import { login } from "store/actions/authAction"
import { showAlert } from "store/reducers/alertReducer"
import loginBg from "assets/images/login/login-bg.webp"
import SAR_logo from "assets/images/login/sar-logo-2.png"
import alyamama_logo from "assets/images/login/alyamama_logo.png"
import rightVector from "assets/images/login/login_right_vector.webp"
import { ChevronDown } from "lucide-react"
import {
  arabicEnglishDigitRegex,
  convertArabicToNumber,
} from "components/Common/Utils"

const Login = props => {
  const { t } = useTranslation()

  //meta title
  document.title = t("Login | SAR")

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loading, isLoading } = useSelector(state => state.Login)
  const [loginType, setLoginType] = useState("id")

  const toggleLoginType = () => {
    setLoginType(loginType === "id" ? "mobile" : "id")
  }

  const validation = useFormik({
    // enableReinitialize : use this  flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      phone: "",
      password: "",
    },
    validationSchema: Yup.object({
      phone: Yup.string()
        .required("Field is required")
        .test(
          "len",
          loginType === "id"
            ? "ID number must be exactly 10 digits"
            : "Mobile number must be 9 to 10 digits",
          val => {
            if (loginType === "id") {
              return val && val.length === 10 && /^\d+$/.test(val)
            } else {
              return (
                val &&
                (val.length === 9 || val.length === 10) &&
                arabicEnglishDigitRegex.test(val)
              )
            }
          }
        ),
      password: Yup.string()
        .required(t("Password is required"))
        .min(8, t("Password must be at least 8 characters long")),
      // .matches(/[A-Z]/, t("Must contain an uppercase letter (A-Z)"))
      // .matches(/[a-z]/, t("Must contain a lowercase letter (a-z)"))
      // .matches(/\d/, t("Must contain a number (0-9)"))
      // .matches(
      //   /[!@#$%^&*(),.?":{}|<>]/,
      //   t("Must contain a special character (@, #, !, ...)")
      // ),
    }),
    onSubmit: values => {
      const { phone, password } = values
      const payload = {
        login_type: loginType === "id" ? "national_id" : "phone_number", // national_id and phone_number
        national_id: convertArabicToNumber(phone),
        phone_code: "+966",
        phone_number: convertArabicToNumber(phone),
        password: password,
      }
      dispatch(login(payload))
        .then(action => {
          const { statusCode, success, message, data } = action?.payload
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }))
            navigate("/profile")
          } else {
            dispatch(showAlert({ text: message, type: "error" }))
          }
        })
        .catch(err => {})
    },
  })

  const LoginProperties = createSelector(
    state => state.Login,
    login => ({
      error: login.error,
    })
  )

  const { error } = useSelector(LoginProperties)

  return (
    <React.Fragment>
      <div className="login_page  h_100 overflow-x-hidden">
        <Container fluid className="h_100">
          <Row>
            <Col lg={6} className="px-0">
              <div className="left_section position-relative">
                <img src={loginBg} className="login_bg" alt="" />
                <div className="login_footer">
                  <img src={SAR_logo} alt="" />
                  <img src={alyamama_logo} alt="" />
                </div>
              </div>
            </Col>
            <Col lg={6} className="px-0">
              <div className="right_section g_center h-100 position-relative">
                <img src={rightVector} alt="" className="login_right_vector" />
                <div className="form_inner">
                  <h1>
                    Seasonal jobs for the train in the <span>holy sites</span>
                  </h1>
                  <p>
                    Don't have an account?{" "}
                    <Link to={"#"}>Click to create a new account</Link>
                  </p>
                  <Form
                    className="form-horizontal"
                    onSubmit={e => {
                      e.preventDefault()
                      validation.handleSubmit()
                      return false
                    }}
                  >
                    {error ? <Alert color="danger">{error}</Alert> : null}

                    <div className="mb-3 mt-3">
                      <Label className="form-label">
                        {loginType === "id"
                          ? t("National Id")
                          : t("Mobile Number")}
                        <span className="text_danger">*</span>
                      </Label>
                      <div className="d-flex align-items-center gap-3">
                        {loginType === "mobile" && (
                          <div className="position-relative">
                            <select name="" id="" className="min_70">
                              <option value="+966">+966</option>
                            </select>
                            <ChevronDown className="select_arrow" />
                          </div>
                        )}
                        <Input
                          name="phone"
                          className="form-control"
                          placeholder={
                            loginType === "id"
                              ? t("National Id")
                              : t("Mobile Number")
                          }
                          type="text"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.phone || ""}
                          invalid={
                            validation.touched.phone && validation.errors.phone
                              ? true
                              : false
                          }
                        />
                      </div>
                      {validation.touched.phone && validation.errors.phone ? (
                        <FormFeedback type="invalid">
                          {validation.errors.phone}
                        </FormFeedback>
                      ) : null}
                    </div>

                    <div className="mb-3">
                      <Label className="form-label">{t("Password")}</Label>
                      <span className="text_danger">*</span>
                      <Input
                        name="password"
                        value={validation.values.password || ""}
                        type="password"
                        placeholder={t("Password")}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          validation.touched.password &&
                          validation.errors.password
                            ? true
                            : false
                        }
                      />
                      {validation.touched.password &&
                      validation.errors.password ? (
                        <FormFeedback type="invalid">
                          {validation.errors.password}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <div>
                      <Link
                        to="/forgot-password"
                        className="forget_password d-block  ms-auto"
                      >
                        {t("Forgot your password?")}
                      </Link>
                    </div>

                    <div className="mt-3 d-grid">
                      <button
                        className={`main_btn ${
                          !validation.isValid ? "disabled-button" : ""
                        }`}
                        type="submit"
                        disabled={isLoading}
                      >
                        {t("Sign in")}
                      </button>
                      <p className="privacy_text">
                        {t("By using this site, you agree to comply with our")}
                        <span className="text_primary">
                          {" "}
                          terms and conditions and privacy policy.
                        </span>
                      </p>
                    </div>

                    <div className="text-center mt-3">
                      <button
                        type="button"
                        className="btn btn-link login_with_text"
                        onClick={toggleLoginType}
                      >
                        {loginType === "id"
                          ? "Login with Mobile Number"
                          : "Login with National Id"}
                      </button>
                    </div>
                  </Form>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withRouter(Login)

Login.propTypes = {
  history: PropTypes.object,
}
